// sof-pdp-qty-price — keeps the PDP price in sync with the currently-selected
// variant's price × PDP qty × cart-aware discount.
//
// Listens for:
//   1. fetch to /cart/(change|add|update|clear) — refreshes cart count
//   2. MutationObserver on the cart bubble — catches section-replace updates
//   3. PDP qty input + buttons — live price on qty change
//   4. Variant-selects change events — live price on size switch (6 ft / 15 ft)
//   5. Dawn's "variant:change" custom event (fallback) + shopify:section:load
(function () {
  function fmt(cents) { return `$${(cents / 100).toFixed(2)}`; }
  function discountFor(totalQty) {
    if (totalQty >= 3) return 15;
    if (totalQty >= 2) return 10;
    return 0;
  }

  let cartCount = 0;
  async function refreshCartCount() {
    try {
      const r = await fetch('/cart.js', { headers: { 'Accept': 'application/json' } });
      if (!r.ok) return;
      const cart = await r.json();
      cartCount = cart.item_count || 0;
    } catch { /* keep previous */ }
  }

  function findPriceEl(wrap) {
    return wrap.querySelector('.price--large') || wrap.querySelector('.price');
  }

  function parseVariantsPrices(wrap) {
    const raw = wrap.getAttribute('data-variants-prices');
    if (!raw) return null;
    const map = {};
    raw.split(',').forEach(pair => {
      const [k, v] = pair.split(':');
      if (k && v) map[k.trim()] = parseInt(v.trim(), 10);
    });
    return Object.keys(map).length ? map : null;
  }

  function getInfoEl(wrap) {
    return wrap.closest('product-info, .product, .section-main-product, body') || document;
  }

  function currentBasePrice(wrap) {
    const map = parseVariantsPrices(wrap);
    if (map) {
      const infoEl = getInfoEl(wrap);
      const idInput = infoEl.querySelector('input[name="id"]');
      const vid = idInput && idInput.value;
      if (vid && map[vid] != null) return parseInt(map[vid], 10);
    }
    const fallback = parseInt(wrap.getAttribute('data-base-price'), 10);
    return Number.isFinite(fallback) ? fallback : null;
  }

  function render(wrap) {
    const basePrice = currentBasePrice(wrap);
    if (basePrice == null) return;

    const priceEl = findPriceEl(wrap);
    if (!priceEl) return;

    const infoEl = getInfoEl(wrap);
    const qtyInput = infoEl.querySelector('.quantity__input[name="quantity"]');
    const pdpQty = qtyInput ? Math.max(1, parseInt(qtyInput.value, 10) || 1) : 1;

    const totalQty = pdpQty + cartCount;
    const discPct = discountFor(totalQty);
    const gross = basePrice * pdpQty;
    const savings = Math.round(gross * discPct / 100);
    const net = gross - savings;

    const priceClasses = priceEl.className;
    if (discPct > 0) {
      priceEl.innerHTML = `
        <div class="price__regular sof-price-inline">
          <s class="sof-cart-was">${fmt(gross)}</s>
          <span class="price-item price-item--regular sof-cart-now">${fmt(net)}</span>
          <span class="sof-pdp-pct">(Save ${discPct}%)</span>
        </div>
      `;
    } else {
      priceEl.innerHTML = `
        <div class="price__regular">
          <span class="price-item price-item--regular">${fmt(gross)}</span>
        </div>
      `;
    }
    priceEl.className = priceClasses;
  }

  function renderAll() {
    document.querySelectorAll('[data-sof-price-wrap]').forEach(render);
  }

  function wire() {
    document.querySelectorAll('[data-sof-price-wrap]').forEach(wrap => {
      render(wrap);
      const infoEl = getInfoEl(wrap);

      // PDP qty input (typing, arrow keys)
      infoEl.querySelectorAll('.quantity__input[name="quantity"]').forEach(input => {
        if (input.dataset.sofBound) return;
        input.dataset.sofBound = '1';
        ['input', 'change'].forEach(ev => input.addEventListener(ev, () => render(wrap)));
      });
      // PDP qty +/- buttons (click → DOM updates async, so 30ms defer)
      infoEl.querySelectorAll('.quantity__button').forEach(btn => {
        if (btn.dataset.sofBound) return;
        btn.dataset.sofBound = '1';
        btn.addEventListener('click', () => setTimeout(() => render(wrap), 30));
      });
      // Variant picker — every input/select with name="id" OR any
      // `.product-form__input` (Dawn wraps variant pickers in this class).
      // Defer 30ms because Dawn fetches a section update on change.
      infoEl.querySelectorAll('input[name="id"], select[name="id"]').forEach(el => {
        if (el.dataset.sofBound) return;
        el.dataset.sofBound = '1';
        el.addEventListener('change', () => setTimeout(() => render(wrap), 30));
      });
      infoEl.querySelectorAll('.product-form__input input, .product-form__input select').forEach(el => {
        if (el.dataset.sofBound) return;
        el.dataset.sofBound = '1';
        el.addEventListener('change', () => setTimeout(() => render(wrap), 50));
      });
      // Dawn's custom variant-change event (some versions dispatch this)
      if (!infoEl.dataset || !infoEl.dataset.sofVariantBound) {
        if (infoEl.addEventListener) {
          infoEl.addEventListener('variant:change', () => setTimeout(() => render(wrap), 30));
          infoEl.addEventListener('variantchange', () => setTimeout(() => render(wrap), 30));
        }
        if (infoEl.dataset) infoEl.dataset.sofVariantBound = '1';
      }
    });
  }

  async function onCartChange() {
    await refreshCartCount();
    renderAll();
  }

  // ── Mechanism 1 — fetch wrapper ───────────────────────────────────── //
  const CART_ENDPOINT_RE = /\/cart\/(change|add|update|clear)(\.js)?/;
  const origFetch = window.fetch;
  window.fetch = async function (input, init) {
    const url = typeof input === 'string' ? input : (input && input.url) || '';
    const isCart = CART_ENDPOINT_RE.test(url);
    const resp = await origFetch.apply(this, arguments);
    if (isCart) {
      setTimeout(onCartChange, 30);
    }
    return resp;
  };

  // ── Mechanism 2 — MutationObserver on the cart bubble ─────────────── //
  function observeCartBubble() {
    const bubble = document.querySelector('#cart-icon-bubble, .cart-count-bubble');
    if (!bubble) return;
    const mo = new MutationObserver(() => onCartChange());
    mo.observe(bubble, { childList: true, characterData: true, subtree: true, attributes: true });
  }

  async function init() {
    await refreshCartCount();
    wire();
    observeCartBubble();
    // Rebind after variant section swaps (Dawn replaces sections on variant change).
    document.addEventListener('shopify:section:load', () => { wire(); observeCartBubble(); });
    document.addEventListener('cart:refresh', onCartChange);
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', init);
  } else {
    init();
  }
})();
